/**
 * contextualMicroInsights.js
 *
 * Generates a one-line behavioral hint for the LLM about what pastoral
 * micro-context to add at the end of a concise or pastoral response.
 *
 * This file does NOT generate the micro-context itself — the LLM does,
 * because it already has the real data in [panorama:], [synthesis:], and [data:].
 * This file tells the LLM what specific signal to look for and surface,
 * phrased as a targeted instruction rather than open-ended advice.
 *
 * Examples of what the LLM might produce from this hint:
 *   "Hace 18 días que no reciben seguimiento."
 *   "Es la tercera oración activa esta semana."
 *   "Esa iglesia no ha tenido actividad en 24 días."
 *   "Tienes 6 visitas pendientes hoy."
 */

import { getDistrictState } from "../kevia-data/districtStateEngine";

// Maps intent → insight types that are relevant to add as micro-context
const INTENT_RELEVANT_TYPES = {
  visit_plan:      ["abandono_pastoral", "seguimiento_atrasado"],
  family_followup: ["abandono_pastoral", "seguimiento_atrasado", "oracion_critica"],
  prayer:          ["oracion_critica"],
  planning:        ["abandono_pastoral", "seguimiento_atrasado", "saturacion_ministerial"],
  analysis:        ["abandono_pastoral", "oracion_critica", "iglesia_inactiva", "pipeline_bautismal"],
  events:          ["saturacion_ministerial"],
};

const SEVERITY_ORDER = { high: 0, medium: 1, low: 2 };

/**
 * Build a targeted micro-insight hint for the LLM.
 * Returns a compact instruction string or null if no hint is warranted.
 *
 * @param {string} intent
 * @param {string} mode  — conversational mode from responseStyleEngine
 * @returns {string|null}
 */
export function buildMicroInsightHint(intent, mode) {
  // Urgent and emotional modes need no additional context — keep them focused
  if (mode === "urgent" || mode === "emotional") return null;

  // Reflective mode (sermon/devotional) breaks with data micro-context
  if (mode === "reflective") return null;

  const relevantTypes = INTENT_RELEVANT_TYPES[intent];
  if (!relevantTypes?.length) return null;

  const state = getDistrictState();
  if (!state?.insights?.length) return null;

  // Find the highest-severity insight matching this intent's relevant types
  const match = state.insights
    .filter((ins) => relevantTypes.includes(ins.type))
    .sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3))[0];

  if (!match) return null;

  return (
    `MICRO-CONTEXTO PASTORAL: Si tu respuesta ya cubre lo principal, ` +
    `añade al final esta observación en una sola línea sin explicarla ni elaborarla: ` +
    `"${match.insight}"`
  );
}
